import React, { useState } from 'react';
import { useDrag } from 'react-dnd';
import { KanbanDeal } from '../types/api';

export const KANBAN_DEAL_TYPE = 'KANBAN_DEAL';

interface KanbanDealCardProps {
  deal: KanbanDeal;
  isExpanded: boolean;
  onToggleExpand: () => void;
  onLaunchAssistant: () => void;
  onCardClick: () => void;
  onMarkDone?: () => void;
}

const formatCurrency = (amount?: number) => {
  if (!amount) return '$0';
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);
};

const formatDate = (dateStr?: string) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
};

const getDaysSince = (dateStr?: string) => {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  const now = new Date();
  return Math.floor((now.getTime() - d.getTime()) / (1000 * 60 * 60 * 24));
};

const getDealIcon = (transactionType?: string, transactionSubType?: string) => {
  const type = (transactionType || '').toLowerCase();
  const subType = (transactionSubType || '').toLowerCase();
  if (subType.includes('smsf')) return 'account_balance';
  if (type.includes('purchase') || type.includes('purch')) return 'home';
  if (type.includes('refinance') || type.includes('refi')) return 'swap_horiz';
  if (type.includes('investment') || subType.includes('investment')) return 'trending_up';
  return 'attach_money';
};

/** Format client folder name for display: "Baker - Luke and Tarryn" → "Luke & Tarryn Baker" */
const formatClientName = (folderName: string) => {
  // Pattern: "Surname - FirstName" or "Surname - First and Second"
  const match = folderName.match(/^(.+?)\s*-\s*(.+)$/);
  if (!match) return folderName;
  const surname = match[1].trim();
  const firstNames = match[2].trim().replace(' and ', ' & ');
  return `${firstNames} ${surname}`;
};

const KanbanDealCard: React.FC<KanbanDealCardProps> = ({
  deal,
  isExpanded,
  onToggleExpand,
  onLaunchAssistant,
  onCardClick,
  onMarkDone
}) => {
  const [hovered, setHovered] = useState(false);

  const [{ isDragging }, dragRef] = useDrag(() => ({
    type: KANBAN_DEAL_TYPE,
    item: { deal },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  }), [deal]);

  const daysSince = getDaysSince(deal.createdDate);
  const nextTask = deal.tasks?.find(t => !t.isCompleted);

  const ageColor = daysSince === null ? '#888'
    : daysSince < 14 ? '#4CAF50'
    : daysSince <= 30 ? '#FF9800'
    : '#f44336';

  const clientName = formatClientName(deal.clientFolder);
  const phone = deal.applicants?.[0]?.phone || deal.primaryContactPhone;
  const email = deal.applicants?.[0]?.email || deal.primaryContactEmail;
  const isLead = !deal.dealFolder;

  return (
    <div
      ref={dragRef as unknown as React.Ref<HTMLDivElement>}
      onClick={onCardClick}
      style={{
        background: 'var(--obsidian-sidebar-bg)',
        border: `1px solid ${hovered ? 'var(--obsidian-accent)' : 'var(--obsidian-border)'}`,
        borderRadius: 8,
        padding: 12,
        marginBottom: 8,
        transition: 'border-color 0.15s ease, opacity 0.15s ease',
        cursor: 'grab',
        opacity: isDragging ? 0.4 : 1,
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Client name (primary title) */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 1, minWidth: 0 }}>
          <span className="material-symbols-outlined" style={{ fontSize: 18, color: 'var(--obsidian-accent)', flexShrink: 0 }}>
            person
          </span>
          <span style={{
            fontSize: 14,
            fontWeight: 600,
            color: 'var(--obsidian-text-primary)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}>
            {clientName}
          </span>
        </div>
        <button
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: 2,
            color: 'var(--obsidian-text-secondary)',
            display: 'flex',
            alignItems: 'center',
            flexShrink: 0
          }}
          title="More options"
        >
          <span className="material-symbols-outlined" style={{ fontSize: 18 }}>more_vert</span>
        </button>
      </div>

      {/* Deal info row (subtitle) */}
      {deal.dealFolder && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
          <span className="material-symbols-outlined" style={{ fontSize: 16, color: 'var(--obsidian-text-secondary)', flexShrink: 0 }}>
            {getDealIcon(deal.transactionType, deal.transactionSubType)}
          </span>
          <span style={{
            fontSize: 12,
            color: 'var(--obsidian-text-secondary)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}>
            {deal.displayName}
          </span>
          {deal.amount ? (
            <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--obsidian-accent)', marginLeft: 'auto', flexShrink: 0 }}>
              {formatCurrency(deal.amount)}
            </span>
          ) : null}
        </div>
      )}

      {/* Lead badge for clients without deals */}
      {isLead && (
        <div style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
          fontSize: 11,
          fontWeight: 600,
          color: '#FF9800',
          background: 'rgba(255, 152, 0, 0.12)',
          borderRadius: 10,
          padding: '2px 8px',
          marginBottom: 8
        }}>
          <span className="material-symbols-outlined" style={{ fontSize: 13 }}>star</span>
          Lead - No active deal
        </div>
      )}

      {/* Contact info row - always show if available */}
      {(phone || email) && (
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 8 }}>
          {phone && (
            <a
              href={`tel:${phone}`}
              style={{ display: 'flex', alignItems: 'center', gap: 4, color: 'var(--obsidian-text-secondary)', textDecoration: 'none', fontSize: 12 }}
              title={phone}
              onClick={(e) => e.stopPropagation()}
            >
              <span className="material-symbols-outlined" style={{ fontSize: 14 }}>call</span>
              <span style={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{phone}</span>
            </a>
          )}
          {email && (
            <a
              href={`mailto:${email}`}
              style={{ display: 'flex', alignItems: 'center', gap: 4, color: 'var(--obsidian-text-secondary)', textDecoration: 'none', fontSize: 12 }}
              title={email}
              onClick={(e) => e.stopPropagation()}
            >
              <span className="material-symbols-outlined" style={{ fontSize: 14 }}>mail</span>
              <span style={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {email.split('@')[0]}
              </span>
            </a>
          )}
        </div>
      )}

      {/* Date + Age row */}
      {deal.createdDate && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          <span style={{ fontSize: 12, color: 'var(--obsidian-text-secondary)' }}>
            {formatDate(deal.createdDate)}
          </span>
          {daysSince !== null && (
            <span style={{
              fontSize: 11,
              fontWeight: 600,
              color: '#fff',
              background: ageColor,
              borderRadius: 10,
              padding: '1px 7px',
              lineHeight: '16px'
            }}>
              {daysSince}d
            </span>
          )}
        </div>
      )}

      {/* Task progress section */}
      {deal.tasksTotal > 0 && (
        <div style={{ marginBottom: 8 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              cursor: 'pointer',
              userSelect: 'none'
            }}
            onClick={onToggleExpand}
          >
            <span style={{ fontSize: 12, color: 'var(--obsidian-text-secondary)' }}>
              {deal.tasksTotal} tasks for this column
            </span>
            <span className="material-symbols-outlined" style={{
              fontSize: 16,
              color: 'var(--obsidian-text-secondary)',
              transition: 'transform 0.2s ease',
              transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)'
            }}>
              expand_more
            </span>
          </div>

          {/* Progress bar - segmented blocks */}
          <div style={{ display: 'flex', gap: 2, marginTop: 6, alignItems: 'center' }}>
            <div style={{ display: 'flex', gap: 2, flex: 1 }}>
              {deal.tasks.map((task, i) => (
                <div
                  key={i}
                  style={{
                    flex: 1,
                    height: 6,
                    borderRadius: 3,
                    background: task.isCompleted ? '#4CAF50' : 'rgba(255,255,255,0.1)'
                  }}
                />
              ))}
            </div>
            <span style={{ fontSize: 11, color: 'var(--obsidian-text-secondary)', marginLeft: 6, flexShrink: 0 }}>
              {deal.tasksCompleted} of {deal.tasksTotal}
            </span>
          </div>

          {/* Expanded task checklist */}
          {isExpanded && (
            <div style={{ marginTop: 8 }}>
              {deal.tasks.map((task, i) => (
                <div key={i} style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 6,
                  padding: '3px 0',
                  fontSize: 12,
                  color: task.isCompleted ? 'var(--obsidian-text-secondary)' : 'var(--obsidian-text-primary)'
                }}>
                  <span className="material-symbols-outlined" style={{
                    fontSize: 16,
                    color: task.isCompleted ? '#4CAF50' : 'var(--obsidian-text-secondary)',
                    flexShrink: 0,
                    marginTop: 1
                  }}>
                    {task.isCompleted ? 'check_box' : 'check_box_outline_blank'}
                  </span>
                  <span style={{
                    textDecoration: task.isCompleted ? 'line-through' : 'none',
                    opacity: task.isCompleted ? 0.7 : 1
                  }}>
                    {task.description}
                  </span>
                </div>
              ))}
            </div>
          )}

          {/* Next task highlight */}
          {!isExpanded && nextTask && (
            <div style={{
              marginTop: 6,
              padding: '6px 8px',
              borderRadius: 6,
              background: 'rgba(var(--obsidian-accent-rgb, 138, 180, 248), 0.1)',
              border: '1px solid rgba(var(--obsidian-accent-rgb, 138, 180, 248), 0.2)',
              fontSize: 12,
              color: 'var(--obsidian-text-primary)',
              display: 'flex',
              alignItems: 'center',
              gap: 4
            }}>
              <span className="material-symbols-outlined" style={{ fontSize: 14, color: 'var(--obsidian-accent)' }}>
                arrow_forward
              </span>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {nextTask.description}
              </span>
            </div>
          )}
        </div>
      )}

      {/* Action buttons row */}
      <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
        {onMarkDone && (
          <button
            onClick={(e) => { e.stopPropagation(); onMarkDone(); }}
            style={{
              flex: 1,
              padding: '6px 10px',
              fontSize: 12,
              fontWeight: 500,
              color: 'var(--obsidian-text-secondary)',
              background: 'transparent',
              border: '1px solid var(--obsidian-border)',
              borderRadius: 6,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 4
            }}
          >
            <span className="material-symbols-outlined" style={{ fontSize: 14 }}>check_circle</span>
            Mark as Done
          </button>
        )}
        <button
          onClick={(e) => { e.stopPropagation(); onLaunchAssistant(); }}
          style={{
            flex: 1,
            padding: '6px 10px',
            fontSize: 12,
            fontWeight: 500,
            color: '#fff',
            background: 'var(--obsidian-accent)',
            border: 'none',
            borderRadius: 6,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 4
          }}
        >
          <span className="material-symbols-outlined" style={{ fontSize: 14 }}>smart_toy</span>
          Launch Assistant
        </button>
      </div>
    </div>
  );
};

export default React.memo(KanbanDealCard);
